import { useState, useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';
import Button from '../generic/Button';
import { icons } from '../generic/Icon';
import Text from '../generic/Text';
import CallToAction from '../CallToAction';
import PoliciesSubnav from './PoliciesSubnav';
import ModalCreatePolicy from './ModalCreatePolicy';
import ModalImportPolicy from './ModalImportPolicy';
import { userHasPermissionToCreatePolicyType } from '../../helpers/permissions';
import utils from '../../utils';
import { window } from '../../globals';
import type { RouterProps } from '../../types';
import { useIsFeatureEnabled } from '../../helpers/features';
import { FULL_QUALITY } from '../../../shared/featureFlags/flagNames';
import { ENTERPRISE_PLATFORM } from '../../../shared/subscriptions';
import { customizeableLicensePoliciesFeature, customizeableSecurityPoliciesFeature } from '../settings/billing/features/consts';
import { PolicyType } from '../../../typings/enums';

type Props = Pick<RouterProps, 'location'>;

/**
 * This component acts as the "call to action" section of
 * the policies page. It allows the user to create a new policy,
 * or import an existing one.
 */
const ListPoliciesCallToAction: VoidFunctionComponent<Props> = ({ location }) => {
  const [shouldShowCreateModal, setShouldShowCreateModal] = useState<boolean>(false);
  const [shouldShowImportModal, setShouldShowImportModal] = useState<boolean>(false);

  const isQualityRestricted = !useIsFeatureEnabled(FULL_QUALITY);

  const policyType = useMemo((): PolicyType => {
    if (location.pathname.includes('/security')) {
      return PolicyType.SECURITY;
    }
    if (location.pathname.includes('/quality')) {
      return PolicyType.QUALITY;
    }
    if (location.pathname.includes('/sbom')) {
      return PolicyType.SBOM;
    }
    return PolicyType.LICENSING;
  }, [location]);

  const toggleCreateModal = (shouldShowModal: boolean) => {
    if (policyType !== PolicyType.QUALITY && !utils.verify_subscription('premium')) {
      const feature = policyType === PolicyType.SECURITY ? customizeableSecurityPoliciesFeature : customizeableLicensePoliciesFeature;
      window.showPlanSelectorModal({ focusFeature: feature });
      return;
    }

    if (policyType === PolicyType.QUALITY && isQualityRestricted) {
      window.showPlanSelectorModal({ tier: ENTERPRISE_PLATFORM });
      return;
    }

    setShouldShowCreateModal(shouldShowModal);
  };

  return (
    <>
      <CallToAction shouldHideBottomBorder>
        <div className="margin-bottom-lg">
          <Text.H1>
            Policies
          </Text.H1>
          <Text.Text>
            View and manage issue policies for your organization.
          </Text.Text>
        </div>

        {userHasPermissionToCreatePolicyType(policyType) && (
          <>
            <Button
              type="primary"
              size="large"
              icon={icons.plusCircle}
              onClick={() => toggleCreateModal(true)}
              testId="fossa-create-policy-button"
            >
              Create Policy
            </Button>
            {window.user?.super && policyType === PolicyType.LICENSING && (
              <span className="margin-left-sm">
                <Button
                  type="slate"
                  size="large"
                  icon={icons.export}
                  onClick={() => setShouldShowImportModal(true)}
                >
                  Import Policy
                </Button>
              </span>
            )}
          </>
        )}
      </CallToAction>
      <PoliciesSubnav />
      {window.user?.super && (
        <ModalImportPolicy
          show={shouldShowImportModal}
          onHide={() => setShouldShowImportModal(false)}
        />)}
      <ModalCreatePolicy
        policyType={policyType}
        show={shouldShowCreateModal}
        onHide={() => setShouldShowCreateModal(false)}
      />
    </>
  );
};

export default ListPoliciesCallToAction;
